import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useLang } from "../i18n";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { MaskedLine, Reveal } from "../components/Reveal";
import { RichText } from "../components/RichText";
import { Marquee } from "../components/Marquee";
import { ShareFab } from "../components/ShareFab";
import { Users, Swords, Radar, Flame, TowerControl, Volume2, MessagesSquare, Compass, Medal, Ban, Handshake, CalendarClock, MessageCircle } from "lucide-react";
import { BadgeIcon, SubBadge, DonorBadge } from "../lib/badges";
import { StaffName } from "../components/StaffName";
import { HSlider } from "../components/HSlider";

const DEVS_VIDEO_ID = "ugkuP4a3xk4";

const GUIDE = [
  { icon: Swords, key: "g1" },
  { icon: Radar, key: "g2" },
  { icon: Flame, key: "g3" },
  { icon: TowerControl, key: "g4" },
  { icon: Volume2, key: "g5" },
  { icon: MessagesSquare, key: "g6" },
  { icon: Compass, key: "g7" },
  { icon: Medal, key: "g8" },
  { icon: Ban, key: "g9" },
];

function Hero() {
  const { t } = useLang();
  const [today, setToday] = useState(0);
  useEffect(() => {
    const load = () => api.get("/roster").then(({ data }) => setToday(data.today || 0)).catch(() => {});
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, []);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "60%"]);

  return (
    <section ref={ref} className="relative min-h-[78vh] flex items-end overflow-hidden scanlines-bg" data-testid="hero-section">
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <div className="absolute inset-0 overflow-hidden">
          <iframe
            src="https://www.youtube-nocookie.com/embed/ZVzF9FEd8GU?autoplay=1&mute=1&controls=0&loop=1&playlist=ZVzF9FEd8GU&modestbranding=1&playsinline=1&rel=0&iv_load_policy=3&disablekb=1&fs=0"
            title=""
            tabIndex={-1}
            aria-hidden="true"
            allow="autoplay; encrypted-media"
            data-testid="hero-bg-video"
            className="absolute left-1/2 top-1/2 aspect-video w-[177.78vh] min-w-full h-full min-h-[56.25vw] -translate-x-1/2 -translate-y-1/2 scale-110 pointer-events-none"
            style={{ filter: "blur(8px) saturate(0.8)", opacity: 0.45 }}
          />
        </div>
        <div className="absolute inset-0 bg-[#050505]/55" />
        <div className="absolute inset-x-0 bottom-0 h-64 bg-[#050505]" style={{ maskImage: "linear-gradient(to top, black 30%, transparent)", WebkitMaskImage: "linear-gradient(to top, black 30%, transparent)" }} />
      </motion.div>

      <div className="absolute top-32 left-5 md:left-8 hud-label animate-blink">● REC — FEED LIVE</div>
      <div className="absolute top-32 right-5 md:right-8 hud-label hidden md:block">LAT 45.9 N // LON 21.2 E — SETTORE KARPATH</div>

      <motion.div style={{ y: textY }} className="relative z-10 max-w-7xl mx-auto px-5 md:px-8 pb-24 pt-48 w-full">
        <p className="hud-label mb-6 text-wardogs" data-testid="hero-overline">{t("hero.overline")}</p>
        <h1 className="font-display font-semibold uppercase leading-[0.85] tracking-tight text-[17vw] md:text-[10rem] lg:text-[12rem]">
          <MaskedLine delay={0.15}><RichText tag="span" text={t("hero.line1")} /></MaskedLine>
          <MaskedLine delay={0.3}><span className="text-wardogs"><RichText tag="span" text={t("hero.line2")} /></span></MaskedLine>
          <MaskedLine delay={0.45} className="text-white/25"><RichText tag="span" text={t("hero.line3")} /></MaskedLine>
        </h1>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-8 max-w-xl text-base md:text-lg text-steel"
        >
          <RichText tag="span" text={t("hero.sub")} />
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.7 }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <a
            href="https://store.steampowered.com/app/1867240/WARDOGS/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-cta-steam"
            className="chamfer bg-wardogs hover:bg-wardogs-hover text-[#050505] font-mono text-xs uppercase tracking-[0.25em] font-bold px-8 py-4 transition-colors duration-300"
          >
            {t("hero.cta2")}
          </a>
          <Link
            to="/discord"
            data-testid="hero-cta-discord"
            className="discord-cta chamfer-sm bg-[#5865F2] hover:bg-[#4752C4] text-white px-7 py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] font-bold flex items-center gap-2.5 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_45px_rgba(88,101,242,0.75)]"
          >
            <img src="/discord-logo.png" alt="Discord" className="w-4 h-4 object-contain" /> {t("hero.discord_cta")}
          </Link>
          {today > 0 && (
            <span className="chamfer-sm border border-wardogs/40 bg-wardogs-dim px-5 py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-wardogs flex items-center gap-2" data-testid="hero-today-count">
              <Users className="w-4 h-4" /> +{today} {t("standings.today")}
            </span>
          )}
        </motion.div>
      </motion.div>
    </section>
  );
}

function LiveBanner() {
  const { t } = useLang();
  const [live, setLive] = useState(null);
  useEffect(() => {
    const check = () =>
      api.get("/live").then(({ data }) => {
        const s = data.streams?.["ilquartiermastro"];
        setLive(s?.is_live ? s : null);
      }).catch(() => {});
    check();
    const id = setInterval(check, 30000);
    return () => clearInterval(id);
  }, []);
  if (!live) return null;
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="border-b border-[#1A1A1A] bg-[#0C0C0C]" data-testid="live-banner">
      <Link to="/streamer" className="max-w-7xl mx-auto px-5 md:px-8 py-3.5 flex items-center gap-4 group">
        <span className="w-2.5 h-2.5 bg-valkyra animate-blink shrink-0" />
        <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-valkyra shrink-0">{t("live.now")}</span>
        <span className="font-mono text-xs text-white truncate" data-testid="live-banner-title">{live.title}</span>
        <span className="hidden md:inline font-mono text-[10px] text-steel shrink-0">{t("live.viewers", { n: live.viewers })}</span>
        <span className="ml-auto font-mono text-[10px] uppercase tracking-[0.2em] text-wardogs group-hover:text-white transition-colors duration-300 shrink-0">{t("live.watch")} →</span>
      </Link>
    </motion.div>
  );
}

function GameGuide() {
  const { t } = useLang();
  return (
    <section className="relative py-28 md:py-36 grid-bg" data-testid="guide-section">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <Reveal>
          <p className="hud-label mb-3 text-wardogs">{t("guide.over")}</p>
          <h2 className="font-display text-5xl md:text-7xl uppercase leading-none mb-5 max-w-3xl"><RichText tag="span" text={t("guide.title")} /></h2>
          <p className="text-steel text-base md:text-lg max-w-2xl mb-16"><RichText tag="span" text={t("guide.intro")} /></p>
        </Reveal>
        <div className="grid gap-px bg-[#1A1A1A] border border-[#1A1A1A] md:grid-cols-2 lg:grid-cols-3">
          {GUIDE.map((ch, i) => (
            <Reveal key={ch.key} delay={Math.min(i, 5) * 0.08} className="bg-[#050505]">
              <div className="relative p-8 h-full overflow-hidden group">
                <span className="outline-number font-display absolute -top-4 right-3 text-7xl leading-none select-none pointer-events-none">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <ch.icon className="w-7 h-7 text-wardogs mb-5 transition-transform duration-300 group-hover:scale-110" />
                <h3 className="font-display text-3xl md:text-4xl uppercase leading-tight mb-3 pr-14"><RichText tag="span" text={t(`${ch.key}.title`)} /></h3>
                <p className="text-base md:text-lg text-steel leading-relaxed"><RichText tag="span" text={t(`${ch.key}.text`)} /></p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-px">
          <div className="grid lg:grid-cols-2 gap-px bg-[#1A1A1A] border border-[#1A1A1A] border-t-0">
            <div className="bg-[#050505] p-8 md:p-10 relative overflow-hidden">
              <span className="outline-number font-display absolute -top-4 right-3 text-7xl leading-none select-none pointer-events-none">10</span>
              <Handshake className="w-6 h-6 text-wardogs mb-5" />
              <h3 className="font-display text-3xl md:text-4xl uppercase leading-tight mb-4 pr-14"><RichText tag="span" text={t("g10.title")} /></h3>
              <p className="text-base md:text-lg text-steel leading-relaxed mb-6"><RichText tag="span" text={t("g10.text")} /></p>
              <div className="flex flex-col gap-2.5">
                {["1", "2", "3"].map((n) => (
                  <div key={n} className="flex items-start gap-3" data-testid={`steam-promise-${n}`}>
                    <span className="font-mono text-[10px] text-wardogs mt-0.5 shrink-0">[{n}]</span>
                    <p className="font-mono text-[11px] uppercase tracking-[0.1em] text-steel">{t(`g10.p${n}`)}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-[#050505] p-8 md:p-10 flex flex-col justify-center">
              <p className="hud-label mb-4">{t("g10.video")}</p>
              <div className="chamfer-sm border border-[#1A1A1A] overflow-hidden">
                <div className="aspect-video">
                  <iframe
                    src={`https://www.youtube-nocookie.com/embed/${DEVS_VIDEO_ID}`}
                    title="WARDOGS — 10 motivi per non comprarlo"
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    data-testid="guide-devs-video"
                  />
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

const ROLE_META = {
  lonestar: { img: "/factions/lonestar.png", color: "#00A3FF" },
  valkyra: { img: "/factions/valkyra.png", color: "#FF3B30" },
  manticore: { img: "/factions/manticore.png", color: "#34C759" },
};

function RoleCard({ roleId, label, selected, locked, onSelect, disabled }) {
  const { t } = useLang();
  const meta = ROLE_META[roleId];
  const blocked = disabled || locked;
  return (
    <button
      onClick={() => onSelect(roleId)}
      disabled={blocked}
      data-testid={`role-select-${roleId}`}
      className={`group relative chamfer border p-8 text-left w-full h-full transition-all duration-300 ${
        selected ? "bg-[#0C0C0C]" : "border-[#1A1A1A] bg-[#050505] hover:bg-[#0C0C0C]"
      } ${blocked ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
      style={{ borderColor: selected ? meta.color : undefined }}
    >
      <div className="absolute top-0 left-0 right-0 h-px" style={{ background: selected ? meta.color : "transparent" }} />
      <img src={meta.img} alt={`${label} emblem`} className="h-14 w-auto mb-6 transition-transform duration-300 group-hover:scale-110" data-testid={`role-emblem-${roleId}`} />
      <p className="font-display text-4xl uppercase leading-none mb-2" style={{ color: selected ? meta.color : "#fff" }}>{label}</p>
      <p className="text-sm text-steel leading-relaxed">{t(`role.${roleId}.desc`)}</p>
      <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.25em]" style={{ color: selected ? meta.color : locked ? "#FF3B30" : "#4A4D51" }}>
        {selected ? t("com.active") : locked ? t("com.locked") : t("com.select")}
      </p>
    </button>
  );
}

const ROSTER_VISIBLE = 5; // max 5 soldati a fazione, poi la lista scorre con la rotellina

function RosterColumn({ roleId, label, members }) {
  const { t } = useLang();
  const meta = ROLE_META[roleId];
  const hidden = Math.max(0, members.length - ROSTER_VISIBLE);
  return (
    <div className="chamfer border border-[#1A1A1A] bg-[#050505] p-6" data-testid={`roster-column-${roleId}`}>
      <div className="flex items-center justify-between mb-6 pb-4 border-b border-[#1A1A1A]">
        <div className="flex items-center gap-3">
          <img src={meta.img} alt="" className="h-7 w-auto" />
          <span className="font-display text-2xl uppercase" style={{ color: meta.color }}>{label}</span>
        </div>
        <span className="font-mono text-xs text-steel" data-testid={`roster-count-${roleId}`}>{t("com.ops", { n: members.length })}</span>
      </div>
      {members.length === 0 ? (
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#4A4D51] py-6 text-center">{t("com.waiting")}</p>
      ) : (
        <>
          {/* lista scrollabile: 5 righe visibili, il resto scorre con la rotellina
              (data-lenis-prevent: lo smooth scroll globale non deve rubare la rotellina) */}
          <ul className="roster-scroll flex flex-col gap-2 overflow-y-auto overscroll-contain pr-1" style={{ maxHeight: 272 }} data-lenis-prevent data-testid={`roster-list-${roleId}`}>
            {members.map((m) => (
              <li
                key={m.twitch_id}
                className="flex items-center gap-3 border border-[#1A1A1A] bg-[#0C0C0C] px-3 py-2.5 hover:border-[#2A2A2A] transition-colors duration-300"
                data-testid={`roster-member-${m.login}`}
              >
                {m.avatar ? (
                  <img src={m.avatar} alt="" className="w-7 h-7 chamfer-sm" />
                ) : (
                  <span className="w-7 h-7 chamfer-sm bg-[#1A1A1A]" />
                )}
                {m.is_staff ? (
                  <Link to={`/operatore/${m.login}`} data-testid={`roster-name-${m.login}`}>
                    <StaffName name={m.display_name} className="font-mono text-xs truncate" />
                  </Link>
                ) : (
                  <Link to={`/operatore/${m.login}`} className="font-mono text-xs text-white truncate hover:text-wardogs transition-colors duration-300" data-testid={`roster-name-${m.login}`}>
                    {m.display_name}
                  </Link>
                )}
                {m.is_new && (
                  <span className="shrink-0 chamfer-sm bg-wardogs text-[#050505] font-mono text-[9px] font-bold uppercase tracking-[0.15em] px-1.5 py-0.5" data-testid={`roster-new-${m.login}`}>
                    {t("com.new_badge")}
                  </span>
                )}
                {m.subscribed && (
                  <SubBadge tier={m.sub_tier} testid={`roster-sub-badge-${m.login}`} />
                )}
                {m.donated && <DonorBadge testid={`roster-donor-${m.login}`} />}
                <BadgeIcon visits={m.visits} testid={`roster-badge-${m.login}`} />
                {(m.streak || 0) >= 3 && (
                  <span className="flex items-center gap-1 font-mono text-[10px] text-wardogs shrink-0" data-testid={`roster-streak-${m.login}`} title="Badge Fedeltà">
                    <Flame className="w-3.5 h-3.5" />{m.streak}
                  </span>
                )}
                <span className="ml-auto w-1.5 h-1.5" style={{ background: meta.color }} />
              </li>
            ))}
          </ul>
          {hidden > 0 && (
            <div className="mt-3 pt-3 border-t border-[#1A1A1A] flex items-center justify-between" data-testid={`roster-scrollhint-${roleId}`}>
              <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#4A4D51]">rotellina per scorrere</span>
              <span className="font-mono text-[10px] text-steel">+{hidden} altri operatori</span>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Factions() {
  const { user, refresh } = useAuth();
  const { t } = useLang();
  const [roster, setRoster] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadRoster = () => api.get("/roster").then(({ data }) => setRoster(data)).catch(() => {});
  useEffect(() => {
    loadRoster();
    const id = setInterval(loadRoster, 10000);
    return () => clearInterval(id);
  }, []);

  const nextChange = user?.next_change_at ? new Date(user.next_change_at) : null;
  const roleLocked = !!(!user?.is_staff && user?.role && nextChange && nextChange > new Date());
  const daysLeft = roleLocked ? Math.ceil((nextChange - new Date()) / 86400000) : 0;

  const selectRole = async (roleId) => {
    if (!user || saving) return;
    setSaving(true);
    try {
      const { data: roleRes } = await api.post("/me/role", { role: roleId });
      await refresh();
      await loadRoster();
      toast.success(t("com.assigned", { role: roleId.toUpperCase() }));
      const ds = roleRes?.discord_role;
      if (ds === "ok") toast.success(t("com.role_discord_ok", { role: roleId.toUpperCase() }));
      else if (ds === "no_discord") toast.info(t("com.role_discord_no"));
      else if (ds === "not_in_guild") toast.info(t("com.role_discord_notmember"));
      else if (ds === "error") toast.warning(t("com.role_discord_error"));
    } catch (err) {
      const detail = err?.response?.data?.detail || "";
      if (err?.response?.status === 429 && detail.startsWith("role_locked:")) {
        toast.error(t("com.locked_toast", { n: detail.split(":")[1] }));
      } else {
        toast.error(t("com.error"));
      }
    } finally {
      setSaving(false);
    }
  };

  const roles = roster?.roles || [
    { id: "lonestar", label: "Lonestar" },
    { id: "valkyra", label: "Valkyra" },
    { id: "manticore", label: "Manticore" },
  ];

  return (
    <section className="py-28 border-t border-[#1A1A1A]" data-testid="factions-section">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <Reveal>
          <p className="hud-label mb-3 text-wardogs">{t("com.over")}</p>
          <h2 className="font-display text-5xl md:text-7xl uppercase leading-none mb-5 max-w-3xl">
            {t("com.title1")} <span className="text-wardogs">{t("com.title2")}</span>
          </h2>
          <p className="text-steel text-base md:text-lg max-w-2xl mb-12">{t("com.text")}</p>
        </Reveal>

        {user && (
          <Reveal>
            <div className="mb-6 flex items-center gap-3" data-testid="factions-welcome">
              {user.avatar && <img src={user.avatar} alt="" className="w-9 h-9 chamfer-sm border border-[#1A1A1A]" />}
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-steel">
                {t("com.welcome", { name: user.display_name })}
              </p>
            </div>
          </Reveal>
        )}

        {user && roleLocked && (
          <Reveal>
            <div className="chamfer border border-[#2A2A2A] bg-[#0C0C0C] px-6 py-4 mb-8 flex items-center gap-3" data-testid="role-cooldown-notice">
              <CalendarClock className="w-5 h-5 text-wardogs shrink-0" />
              <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-steel">
                {t("com.cooldown", { role: user.role, days: daysLeft })}
              </p>
            </div>
          </Reveal>
        )}

        <HSlider testid="factions-slider" className="mb-20">
          {roles.map((r) => (
            <div key={r.id} className="snap-start shrink-0 w-full sm:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)]">
              <RoleCard
                roleId={r.id}
                label={r.label}
                selected={user?.role === r.id}
                locked={roleLocked && user?.role !== r.id}
                onSelect={selectRole}
                disabled={!user}
              />
            </div>
          ))}
        </HSlider>

        {user && !user.discord_linked && (
          <Reveal>
            <div className="chamfer border border-[#5865F2]/40 bg-[#0C0C0C] px-6 py-5 mb-20 flex flex-col sm:flex-row items-start sm:items-center gap-4" data-testid="discord-link-notice">
              <MessageCircle className="w-6 h-6 text-[#5865F2] shrink-0" />
              <div className="flex-1">
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-white mb-1.5">{t("com.discord_notice_title")}</p>
                <p className="text-sm text-steel leading-relaxed">{t("com.discord_notice_text")}</p>
              </div>
              <Link to="/profilo" className="chamfer-sm border border-[#5865F2] text-[#8B9DF0] hover:bg-[#5865F2] hover:text-white px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors duration-300 shrink-0" data-testid="discord-notice-cta">
                {t("com.discord_notice_cta")}
              </Link>
            </div>
          </Reveal>
        )}

        <Reveal>
          <div className="flex items-end justify-between mb-10">
            <p className="hud-label text-wardogs">{t("com.roster_over")}</p>
            <div className="flex items-center gap-4 font-mono text-xs text-steel" data-testid="roster-total">
              {(roster?.today || 0) > 0 && (
                <div className="relative group" data-testid="roster-today-wrap">
                  <button type="button" className="text-wardogs cursor-pointer outline-none" data-testid="roster-today-badge">
                    +{roster.today} {t("standings.today")}
                  </button>
                  <div
                    className="invisible opacity-0 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100 transition-opacity duration-200 absolute right-0 top-full mt-2 z-40 w-64 chamfer border border-wardogs/40 bg-[#050505]/95 backdrop-blur-md p-4"
                    data-testid="roster-today-popover"
                  >
                    <p className="hud-label mb-3">{t("com.today_names")}</p>
                    <ul className="flex flex-col gap-2 max-h-56 overflow-y-auto" data-lenis-prevent>
                      {(roster.today_users || []).map((u) => (
                        <li key={u.login} className="flex items-center gap-2.5" data-testid={`roster-today-${u.login}`}>
                          {u.avatar ? (
                            <img src={u.avatar} alt="" className="w-6 h-6 chamfer-sm" />
                          ) : (
                            <span className="w-6 h-6 chamfer-sm bg-[#1A1A1A]" />
                          )}
                          <span className="font-mono text-xs text-white truncate">{u.display_name}</span>
                          {u.role && <span className="ml-auto w-1.5 h-1.5 shrink-0" style={{ background: ROLE_META[u.role]?.color || "#FF5C00" }} />}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
              <span className="flex items-center gap-2">
                <Users className="w-4 h-4 text-wardogs" />
                {roster ? t("com.enrolled", { n: roster.total }) : "…"}
              </span>
            </div>
          </div>
        </Reveal>
        <div className="grid gap-6 md:grid-cols-3">
          {roles.map((r) => (
            <RosterColumn key={r.id} roleId={r.id} label={r.label} members={roster?.roster?.[r.id] || []} />
          ))}
        </div>
      </div>
    </section>
  );
}

function LiveEventBanner() {
  const [liveEv, setLiveEv] = useState(null);
  useEffect(() => {
    const load = () =>
      api.get("/game-events")
        .then(({ data }) => setLiveEv(data.events.find((e) => e.is_live) || null))
        .catch(() => {});
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, []);
  if (!liveEv) return null;
  return (
    <Link
      to={`/eventi/${liveEv.slug}`}
      className="block border-y border-valkyra/50 bg-valkyra/10 hover:bg-valkyra/20 transition-colors duration-300"
      data-testid="live-event-banner"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-4 flex items-center justify-center gap-4 flex-wrap text-center">
        <span className="relative flex h-2.5 w-2.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-valkyra opacity-75" />
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-valkyra" />
        </span>
        <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-white font-bold">
          In onda ora — {liveEv.name}
        </span>
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-wardogs">
          Guardala con la community →
        </span>
      </div>
    </Link>
  );
}

// Countdown in cima alla home negli ultimi 7 giorni prima del prossimo evento
function NextEventBanner() {
  const [ev, setEv] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [top, setTop] = useState(112);
  useEffect(() => {
    const load = () =>
      api.get("/game-events")
        .then(({ data }) => setEv(data.events.find((e) => !e.is_past && !e.is_live) || null))
        .catch(() => {});
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, []);
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  useEffect(() => {
    // misura l'altezza reale dell'header fisso (la barra early-access sparisce da sola al lancio)
    const measure = () => {
      const h = document.querySelector("header");
      if (h) setTop(h.offsetHeight);
    };
    measure();
    const id = setInterval(measure, 2000);
    window.addEventListener("resize", measure);
    return () => { clearInterval(id); window.removeEventListener("resize", measure); };
  }, []);
  if (!ev) return null;
  const secs = Math.floor((new Date(ev.date_it).getTime() - now) / 1000);
  if (secs <= 0 || secs > 7 * 86400) return null;
  const dd = Math.floor(secs / 86400);
  const hh = Math.floor((secs % 86400) / 3600);
  const mm = Math.floor((secs % 3600) / 60);
  const ss = secs % 60;
  return (
    <Link
      to={`/eventi/${ev.slug}`}
      style={{ marginTop: top }}
      className="block border-b border-[#1A1A1A] bg-[#0C0C0C] hover:bg-[#121212] transition-colors duration-300"
      data-testid="next-event-banner"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-3.5 flex items-center justify-center gap-x-4 gap-y-1.5 flex-wrap text-center">
        <CalendarClock className="w-4 h-4 shrink-0" style={{ color: ev.color }} />
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-steel">Prossimo evento</span>
        <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-white font-bold">{ev.name}</span>
        <span className="font-mono text-[11px] font-bold tabular-nums" style={{ color: ev.color }} data-testid="next-event-countdown">
          {pad2(dd)}g : {pad2(hh)}h : {pad2(mm)}m : {pad2(ss)}s
        </span>
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-wardogs">Entra nella sala →</span>
      </div>
    </Link>
  );
}

const pad2 = (n) => String(Math.max(0, n)).padStart(2, "0");

function PatchBadge() {
  const { t } = useLang();
  const [patch, setPatch] = useState(null);
  useEffect(() => {
    api.get("/patch-notes").then(({ data }) => {
      const latest = (data.items || [])[0];
      if (latest && Date.now() / 1000 - latest.date < 3 * 86400) setPatch(latest);
    }).catch(() => {});
  }, []);
  if (!patch) return null;
  return (
    <Link to="/news" className="block border-b border-wardogs/50 bg-wardogs/10 hover:bg-wardogs/15 transition-colors duration-300" data-testid="patch-badge">
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-3 flex flex-wrap items-center gap-3">
        <span className="relative flex h-2.5 w-2.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-wardogs opacity-70" />
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-wardogs" />
        </span>
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] font-bold text-wardogs">{t("pn.badge")}</p>
        <p className="font-mono text-[11px] text-white truncate">{patch.title}</p>
        <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-steel hidden sm:inline">— {t("pn.badge_sub")}</span>
        <span className="ml-auto font-mono text-[10px] uppercase tracking-[0.15em] text-wardogs">{t("pn.badge_cta")} →</span>
      </div>
    </Link>
  );
}

// Striscia sponsor in fondo alla home: versione ridotta del banner LagoFast,
// porta alla pagina /lagofast (dove ci sono link partner e codice sconto)
function SponsorStrip() {
  const { t } = useLang();
  return (
    <section className="border-t border-[#1A1A1A] py-14" data-testid="sponsor-strip">
      <div className="max-w-5xl mx-auto px-5 md:px-8">
        <Reveal>
          <Link
            to="/lagofast"
            onClick={() => api.post("/sponsor/click", { source: "home_strip" }).catch(() => {})}
            data-testid="sponsor-strip-link"
            className="group chamfer border border-[#1A1A1A] hover:border-wardogs/50 bg-[#0C0C0C] p-5 md:p-6 flex flex-col sm:flex-row items-center gap-5 md:gap-8 transition-all duration-300 hover:shadow-[0_0_40px_rgba(255,106,0,0.12)]"
          >
            <img
              src="/lagofast-banner.webp"
              alt="LagoFast × WARDOGS"
              data-testid="sponsor-strip-banner"
              className="w-full sm:w-44 md:w-52 shrink-0 chamfer-sm border border-[#1A1A1A] aspect-[4/5] sm:aspect-square object-cover object-top transition-transform duration-500 group-hover:scale-[1.02]"
            />
            <div className="text-center sm:text-left min-w-0">
              <p className="hud-label mb-2">{t("home.sponsor.over")}</p>
              <p className="font-display text-2xl md:text-3xl uppercase text-white leading-tight mb-2" data-testid="sponsor-strip-title">
                {t("home.sponsor.title")}
              </p>
              <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-steel">
                {t("home.sponsor.sub")}
              </p>
            </div>
            <span className="sm:ml-auto shrink-0 chamfer-sm bg-wardogs group-hover:bg-wardogs-hover text-[#050505] font-mono text-[10px] uppercase tracking-[0.2em] font-bold px-5 py-3 transition-colors duration-300" data-testid="sponsor-strip-cta">
              {t("home.sponsor.cta")} →
            </span>
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  const { t } = useLang();
  const { refresh } = useAuth();
  const [params] = useSearchParams();
  useEffect(() => {
    if (params.get("auth") === "ok") {
      refresh();
      toast.success(t("com.auth_ok"));
    }
    if (params.get("auth") === "denied") toast.error(t("com.auth_denied"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);
  return (
    <main>
      <NextEventBanner />
      <Hero />
      <PatchBadge />
      <LiveEventBanner />
      <LiveBanner />
      <Marquee />
      <Factions />
      <GameGuide />
      <SponsorStrip />
      <ShareFab />
    </main>
  );
}
